/*
  Список с серверной пагинацией (21.10.2026, требование владельца: «пагинация должна быть
  серверной, а не на фронте»).

  Страницы режет сервер: кабинет просит окно (`limit`, `offset`) и получает `{ items,
  total }`. Хук держит номер страницы и размер, перечитывает окно при их смене и отдаёт
  экрану готовые строки и свойства панели `PageBar`. Прежний `usePaging` резал готовый
  список в браузере и остаётся только там, где списка на сервере нет (расчёт, который
  сервер отдаёт одним ответом и не хранит).

  `load(window)` принимает `{ limit, offset }` и возвращает обещание с `{ items, total }`;
  обязан быть устойчивым между отрисовками (`useCallback`) - иначе список грузился бы
  заново на каждую. Размер по умолчанию - 10 (решение владельца), выбранный человеком
  запоминается один на все списки (`usePaging.js`).

  Что хук делает сверх очевидного:
  - пока грузится следующая страница, прежние строки остаются на экране (`loading`), а не
    мигают пустотой;
  - если после перечитывания страница оказалась за концом (убрали последнюю строку
    последней страницы), хук сам переходит на последнюю существующую, а не показывает
    пустое окно при ненулевом числе записей;
  - ответ запоздавшего запроса, который уже не нужен (человек успел перейти дальше),
    отбрасывается: иначе медленный ответ прежней страницы затирал бы новую.
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { rememberPageSize, storedPageSize } from './usePaging.js'

export const DEFAULT_SIZE = 10

/*
  `refreshKey` (необязательный, 21.10.2026) - значение, смена которого перечитывает ТЕКУЩУЮ
  страницу, не сбрасывая её номер: страница-родитель меняет его после действия, которое могло
  изменить список (отметили поданными, внесли событие). Вместо него в зависимости `load` ничего
  класть не надо: функция загрузки, не читающая значение, получила бы лишнюю зависимость.

  `resetKey` (необязательный, 23.10.2026, находка проверки) - значение, смена которого
  начинает список С ПЕРВОЙ страницы: новый отбор или новая загрузка в журнале шага - другой
  список, и «страница 3» прежнего здесь ничего не значит. Сброс идёт во время отрисовки, до
  запроса: иначе ушёл бы лишний запрос прежней страницы с новым отбором.

  `answer` - весь последний ПРИНЯТЫЙ ответ (23.10.2026): у исполнения и радара рядом со
  страницей приходит сводка, и брать её можно только отсюда. Сводка, переданная наверх изнутри
  `load`, приходила и от ответа, который хук потом отбросил как устаревший, - и затирала суммы.

  `onAccepted(answer)` (необязательный, 23.10.2026) - тот же принятый ответ, но в момент
  принятия, вместе со строками. Сводку наверх передавать им, а не эффектом по `answer`: эффект
  срабатывал на отрисовку позже таблицы, и строки радара уже стояли на экране, а переключатель
  «Горит 1» ещё нет - проверка ловила это через раз. Вызов из того же обработчика ответа
  попадает с новыми строками в одну отрисовку.
*/
export default function usePagedList(
  load,
  initialSize = DEFAULT_SIZE,
  refreshKey = 0,
  resetKey = null,
  onAccepted = null,
) {
  const [size, setSize] = useState(() => storedPageSize(initialSize))
  const [page, setPage] = useState(0)
  const [state, setState] = useState({
    items: null,
    total: 0,
    answer: null,
    error: null,
    loading: true,
  })
  const [tick, setTick] = useState(0)
  const [seenReset, setSeenReset] = useState(resetKey)
  const latest = useRef(0)
  // Через ссылку, а не зависимостью эффекта: новая функция на каждой отрисовке родителя
  // перечитывала бы страницу без причины.
  const accepted = useRef(onAccepted)
  accepted.current = onAccepted

  if (seenReset !== resetKey) {
    setSeenReset(resetKey)
    setPage(0)
  }

  useEffect(() => {
    const ticket = ++latest.current
    setState((was) => ({ ...was, loading: true }))
    load({ limit: size, offset: page * size })
      .then((answer) => {
        if (ticket !== latest.current) return
        const last = Math.max(0, Math.ceil(answer.total / size) - 1)
        if (answer.items.length === 0 && answer.total > 0 && page > last) {
          setPage(last)
          return
        }
        accepted.current?.(answer)
        setState({ items: answer.items, total: answer.total, answer, error: null, loading: false })
      })
      .catch((error) => {
        if (ticket !== latest.current) return
        // Строки, уже стоящие на экране, остаются (аудит 26.10.2026, причина 1): сбой
        // перечитывания не должен уносить с собой таблицу и отметки в ней. Экран, которому
        // нечего показывать (`rows === null`), по-прежнему выводит одну плашку.
        setState((was) => ({ ...was, error, loading: false }))
      })
  }, [load, size, page, tick, refreshKey])

  const reload = useCallback(() => setTick((value) => value + 1), [])

  const onSize = (value) => {
    const next = Number(value)
    rememberPageSize(next)
    setSize(next)
    setPage(0)
  }

  return {
    rows: state.items,
    total: state.total,
    answer: state.answer,
    error: state.error,
    loading: state.loading,
    reload,
    bar: {
      total: state.total,
      size,
      page,
      pages: Math.max(1, Math.ceil(state.total / size)),
      onPage: setPage,
      onSize,
    },
  }
}
